/**
 * CallData.js
 * Talkroute
 *
 * Created by Vel Genov on 2/19/19
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import moment, { Moment } from "moment";
import { parsePhoneNumberFromString } from "libphonenumber-js";

import {
  getFormattedDateAndFullTime,
  getFormattedDate,
  getSimpleFormattedDate as formatDate,
  momentDateToTime
} from "../../helpers/functions/dateFormatter";
import { COUNTRY_CODE } from "./TalkroutePhoneNumber";
import { trimPhoneNumberPlusSign } from "@helpers/functions/phoneNumberFormatter";

// The number of related calls to display to the user
const TOP_CALLS_NUMBER = 5;

/**
 * A general call class
 *
 * @param date the date and time the call was made on
 * @param callerCName the callerCName
 * @param callerNumber the caller number
 * @param duration the call duration
 * @param id the call id
 *
 */
export class CallData {
  public id: number;
  public date: Moment;
  public callerCName: string;
  public callerNumber: string;
  public duration: number;

  constructor(opts) {
    this.date = moment(opts.date);
    this.callerCName = opts.callerCName;
    this.callerNumber = trimPhoneNumberPlusSign(opts.callerNumber);
    this.duration = opts.duration;
    this.id = opts.id;
  }

  /**
   * Get the formatted date and time
   *
   * @returns {*} a date and time string formatted in the following format MM/DD/YYYY h:mm:ss a
   */
  getSimpleFormattedDateAndTime() {
    return getFormattedDateAndFullTime(this.date);
  }

  /**
   * Get the formatted date and time
   *
   * @returns {*} a date and time string formatted in the following format MM/DD/YYYY hh:mm:ss
   */
   getSimpleFormattedDateAndFullTime() {
    return getFormattedDateAndFullTime(this.date);
  }

  /**
   * Get the formatted date
   *
   * @return {*} a date and time string formatted as Today, Yesterday... etc.
   */
  getFormattedDate() {
    return getFormattedDate(this.date);
  }

  getFormattedDatev2() {
    return formatDate(this.date)
  }

  getFormattedTime() {
    return momentDateToTime(this.date)
  }
}

/**
 * Formats a passed in phone number. If not a phone number, then returns the passed in value
 *
 * @returns Either a String of the passed in phone number, or a passed in value, if
 * the passed in value can't be parsed as a phone number
 */
export const formatPhoneNumber = (phoneNumber: string) => {
  try {
    const noPlusNumber = phoneNumber?.replace("+", "");
    const updatedPhoneNumber =
      noPlusNumber?.charAt(0) === COUNTRY_CODE
        ? noPlusNumber
        : `${COUNTRY_CODE} ${noPlusNumber}`;

    const parsedPhoneNumber = parsePhoneNumberFromString(
      `+${updatedPhoneNumber}`
    );
    if (parsedPhoneNumber) {
      return `${COUNTRY_CODE} ${parsedPhoneNumber!.formatNational()}`;
    }
  } catch (error) {
    console.error(error);
  }
  return phoneNumber;
};

/**
 * Validates a passed in phone number
 *
 * @returns Returns true if valid, false if invalid
 */
export const validatePhoneNumber = (phoneNumber) => {
  try {
    const updatedPhoneNumber =
      phoneNumber.charAt(0) === COUNTRY_CODE
        ? phoneNumber
        : `${COUNTRY_CODE} ${phoneNumber}`;

    const parsedPhoneNumber = parsePhoneNumberFromString(
      `+${updatedPhoneNumber}`,
      "US"
    );
    return parsedPhoneNumber?.isValid();
  } catch (error) {
    return false;
  }
};

/**
 * Find a list of top related calls. Also sort the list by date.
 * The number of top calls set to 5 by default. Controlled by the
 * TOP_CALLS_NUMBER constant.
 * @param call the call to search by
 * @return a list of top related calls, null if such doesn't exist
 */
export const getTopRelatedCalls = (callHistory, call) => {
  if (!callHistory || !call) {
    return [];
  }

  const allRelatedCalls = getAllRelatedCalls(callHistory, call);

  let topRelatedCalls = allRelatedCalls;
  if (Array.isArray(allRelatedCalls) && allRelatedCalls.length > 1) {
    topRelatedCalls.sort(
      (a: any, b: any) =>
        // Turn strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        b.date - a.date
    );
    topRelatedCalls = allRelatedCalls.slice(0, TOP_CALLS_NUMBER);
  }

  return topRelatedCalls;
};

/**
 * Find a list of all related calls
 * @param call the call to search by
 * @return a list of all related calls, null if such doesn't exist
 */
const getAllRelatedCalls = (callHistory, call) => {
  let relatedCalls: any = [];
  if (Array.isArray(callHistory) && callHistory.length > 0) {
    const callPhoneNumber = call.getCallerPhoneNumber();

    relatedCalls = callHistory.filter((callRecord) => {
      const callRecordPhoneNumber = callRecord.getCallerPhoneNumber();

      // If call has a numberDialed field, check if it equals to callRecord.numberDialed
      // Otherwise don't use for comparison
      const numberDialedMatches = call.numberDialed
        ? callRecord.numberDialed === call.numberDialed
        : true;

      if (callRecordPhoneNumber === callPhoneNumber && numberDialedMatches) {
        return true;
      }
      return false;
    });
  }

  return relatedCalls;
};

export const truncateLastMessageText = (lastMessage: string, maxLength: number): string => {
  if (lastMessage.length > maxLength) {
    return `${lastMessage.substring(0, maxLength - 3)}...`;
  }
  return lastMessage;
};
